import React from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const COLORS: { label: string; value: string; bar: string; btn: string }[] = [
  { label: 'Blue',   value: 'primary', bar: 'bg-blue-500',  btn: 'bg-blue-500 hover:bg-blue-600'  },
  { label: 'Green',  value: 'success', bar: 'bg-green-500', btn: 'bg-green-500 hover:bg-green-600' },
  { label: 'Amber',  value: 'warning', bar: 'bg-amber-400', btn: 'bg-amber-400 hover:bg-amber-500' },
  { label: 'Red',    value: 'danger',  bar: 'bg-red-500',   btn: 'bg-red-500 hover:bg-red-600'    },
  { label: 'Cyan',   value: 'info',    bar: 'bg-cyan-500',  btn: 'bg-cyan-500 hover:bg-cyan-600'  },
];

const ProgressBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const value: number = Math.min(100, Math.max(0, block.data?.value ?? 50));
  const label: string = block.data?.label ?? '';
  const color: string = block.data?.color ?? 'primary';
  const c = COLORS.find((x) => x.value === color) ?? COLORS[0];

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 space-y-3">
        {/* Label + value */}
        <div className="flex items-center gap-2">
          <input
            autoFocus
            type="text"
            value={label}
            placeholder="Label (optional)"
            onChange={(e) => onChange({ data: { ...block.data, label: e.target.value } })}
            className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-blue-400"
          />
          <span className="text-sm font-semibold text-gray-500 w-10 text-right">{value}%</span>
        </div>

        {/* Slider */}
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange({ data: { ...block.data, value: Number(e.target.value) } })}
          className="w-full accent-blue-600"
        />

        {/* Colour picker */}
        <div className="flex flex-wrap gap-1.5">
          {COLORS.map((col) => (
            <button
              key={col.value}
              onClick={() => onChange({ data: { ...block.data, color: col.value } })}
              className={`text-xs text-white px-2.5 py-1 rounded transition-colors ${col.btn} ${
                color === col.value ? 'ring-2 ring-offset-1 ring-blue-400' : 'opacity-70'
              }`}
            >
              {col.label}
            </button>
          ))}
        </div>

        <div>
          <button
            onClick={onFinish}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {label && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">{label}</span>
          <span className="text-sm font-semibold text-gray-500">{value}%</span>
        </div>
      )}
      <div className="w-full bg-gray-100 rounded-full overflow-hidden" style={{ height: 10 }}>
        <div
          className={`h-full rounded-full transition-all ${c.bar}`}
          style={{ width: `${value}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {!label && <p className="text-right text-sm text-gray-400">{value}%</p>}
    </div>
  );
};

export default ProgressBlock;
